





































































































































import { Component, Vue, Watch } from "vue-property-decorator";
import { Action, Getter } from "vuex-class";
import { PlayerAction, PlayerGetter } from "@/components/player/types";
import { Namespace } from "@/store/types";
import { keyCodeFinder } from "@/services/keyCodeService/keyCodeService";
import SpatialNavigation from "spatial-navigation-js";
import { getBreakpoint, MediaQuery } from "@/helpers/utils";
import dayjs, { Dayjs } from "dayjs";
import {
  ChannelChangeAction,
  ChannelChangeGetter
} from "@/components/channelChange/types";
import { OverlayGetter, TimelineDirection } from "@/components/overlay/types";
import { EpgItem, Episode, Movie } from "@/services/apiService/api.types";
import { EventBus } from "@/main";
import { PlayerControlsEnum } from "@/helpers/constants";

@Component({})
export default class PlayerControls extends Vue {
  @Action(PlayerAction.UpdatePause, { namespace: Namespace.Player })
  updatePause!: () => void;

  @Action(ChannelChangeAction.UpdateOffset, {
    namespace: Namespace.ChannelChange
  })
  updateOffset!: (offset: number) => void;

  @Getter(PlayerGetter.GetPause, { namespace: Namespace.Player })
  getPause!: boolean;

  @Getter(ChannelChangeGetter.GetOffset, { namespace: Namespace.ChannelChange })
  getOffset!: number;

  @Getter(OverlayGetter.GetActiveEpgItem, {
    namespace: Namespace.Overlay
  })
  getActiveEpgItem!: EpgItem;

  @Getter(OverlayGetter.GetActiveVodItem, {
    namespace: Namespace.Overlay
  })
  getActiveVodItem!: Movie | Episode | null;

  @Getter(OverlayGetter.GetActiveVodItemProgressDuration, {
    namespace: Namespace.Overlay
  })
  getActiveVodItemProgressDuration!: number | null;

  @Getter(OverlayGetter.GetActiveVodItemDuration, {
    namespace: Namespace.Overlay
  })
  getActiveVodItemDuration!: number | null;

  //holding timestamp of when video was paused
  pauseTime: Dayjs = dayjs();

  //vars for rewind/fast forward click and hold timer and amount of offset respectively
  timelineSkimmingTimer = 0;
  timelineSkimmingOffset = 0;
  timelineDirection = TimelineDirection;

  get playPauseIcon(): string {
    return this.getPause ? "play" : "pause";
  }

  get secondaryIconsSize(): string {
    switch (getBreakpoint()) {
      case MediaQuery.xl:
        return "5x";
      case MediaQuery.lg:
        return "3x";
      case MediaQuery.md:
        return "2x";
      default:
        return "5x";
    }
  }

  get playPauseIconsSize(): string {
    switch (getBreakpoint()) {
      case MediaQuery.xxl:
      case MediaQuery.xl:
        return "9x";
      case MediaQuery.lg:
        return "7x";
      case MediaQuery.md:
        return "4x";
      default:
        return "2x";
    }
  }

  mounted(): void {
    SpatialNavigation.focus("#playPause");
  }

  pause(event: KeyboardEvent): void {
    if (
      event.type === "click" ||
      ["KEY_ENTER", "KEY_SPACE"].includes(
        keyCodeFinder(event.keyCode) as string
      )
    ) {
      this.updatePause();
    }
  }

  resetOffset(toBeginning = false, event: KeyboardEvent): void {
    if (
      event.type === "click" ||
      ["KEY_ENTER"].includes(keyCodeFinder(event.keyCode) as string)
    ) {
      if (!this.getActiveVodItem) {
        if (!toBeginning) {
          this.updateOffset(0);
          return;
        }

        const offsetToBeginning = dayjs().diff(
          this.getActiveEpgItem.epgStarttime,
          "second"
        );

        this.updateOffset(offsetToBeginning);
      }

      if (this.getActiveVodItem && this.getActiveVodItem.id !== 0) {
        if (!toBeginning) {
          EventBus.$emit(PlayerControlsEnum.End);
          return;
        }

        EventBus.$emit(PlayerControlsEnum.Start);
      }
    }
  }

  timelineSkimmingStart(): void {
    if (!this.timelineSkimmingTimer) {
      this.timelineSkimmingOffset = 15;
      this.timelineSkimmingTimer = setInterval(() => {
        this.timelineSkimmingOffset += 15;
      }, 100);
    }
  }

  timelineSkimmingStop(): void {
    clearInterval(this.timelineSkimmingTimer);
    this.timelineSkimmingTimer = 0;
  }

  timelineSkimmingHandler(direction: TimelineDirection): void {
    this.timelineSkimmingStop();
    if (!this.getActiveVodItem) {
      /*
       *if the value calculated below is negative it simply means that offset is 0 - channel is back ot live
       * offset value issues are handled in the appropriate actions
       */
      if (direction === TimelineDirection.FastForward) {
        this.updateOffset(this.getOffset - this.timelineSkimmingOffset);
        this.timelineSkimmingOffset = 0;
        return;
      }

      this.updateOffset(this.getOffset + this.timelineSkimmingOffset);
      this.timelineSkimmingOffset = 0;
    }

    if (this.getActiveVodItem && this.getActiveVodItem.id !== 0) {
      if (direction === TimelineDirection.FastForward) {
        if (
          this.timelineSkimmingOffset + this.getActiveVodItemProgressDuration! >
          this.getActiveVodItemDuration!
        ) {
          EventBus.$emit(PlayerControlsEnum.End);
          this.timelineSkimmingOffset = 0;
          return;
        }

        EventBus.$emit(
          PlayerControlsEnum.FastForward,
          this.timelineSkimmingOffset
        );
        this.timelineSkimmingOffset = 0;
        return;
      }

      if (
        this.getActiveVodItemProgressDuration! - this.timelineSkimmingOffset <
        0
      ) {
        EventBus.$emit(PlayerControlsEnum.Start);
        this.timelineSkimmingOffset = 0;
        return;
      }

      EventBus.$emit(PlayerControlsEnum.Rewind, this.timelineSkimmingOffset);
      this.timelineSkimmingOffset = 0;
      return;
    }
  }

  @Watch("getPause")
  handlePause(newVal: boolean): void {
    if (!newVal) {
      const offset = dayjs().diff(this.pauseTime, "s");
      this.updateOffset(this.getOffset + offset); //accounting for offset already applied
      return;
    }

    this.pauseTime = dayjs();
  }
}
